import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { YalolaLogo } from "@/components/YalolaLogo";
import { LangFlags } from "@/components/LangFlags";
import { SiteFooter } from "@/components/SiteFooter";
import { StatusBarShim } from "@/components/StatusBarShim";
import { NavBarBackdrop } from "@/components/NavBarBackdrop";

const BACK_LABEL = { fr: "Retour", en: "Back", es: "Volver", pt: "Voltar" };

// Gabarit UNIQUE des pages publiques / légales (Découvrir, CGU, Confidentialité, RGPD, Sécurité des enfants,
// Supprimer / Récupérer mon compte, À propos, Contact…) — web, iOS et Android :
// · Safe Area haut (en-tête épinglé sous la barre système) ET bas (rien sous les boutons Android / la barre iOS) ;
// · en-tête cohérent : flèche retour + logo Yalola + sélecteur FR/EN/ES/PT (choix mémorisé, suivi par toutes les pages) ;
// · `actions` : boutons propres à la page (ex. Se connecter / Rejoindre) — en ligne sur PC, 2e rangée sur téléphone.
export const PublicPageShell = ({ testId, title, subtitle, icon, children, joinLabel, actions, maxWidth = "max-w-2xl", footer = true }) => {
  const { lang, user, config } = useApp();
  const nav = useNavigate();
  const appName = config?.settings?.app_name || "Yalola";
  const home = user ? "/home" : "/";
  const goBack = () => ((window.history.state?.idx ?? 0) > 0 ? nav(-1) : nav(home));
  return (
    <div className="min-h-[100dvh] flex flex-col" style={{ background: "var(--yalola-bg)", paddingBottom: "var(--sa-bottom)" }} data-testid={testId}>
      <StatusBarShim />
      <NavBarBackdrop />
      <header className="sticky top-0 z-20 border-b" style={{ background: "var(--yalola-surface, #fff)", borderColor: "var(--yalola-border)", paddingTop: "var(--sa-top)" }} data-testid={`${testId}-header`}>
        <div className={`${maxWidth} mx-auto px-2 sm:px-4 py-2 flex items-center gap-1 sm:gap-2`}>
          <button type="button" onClick={goBack} aria-label={BACK_LABEL[lang] || BACK_LABEL.fr} className="h-9 w-9 shrink-0 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-text)" }} data-testid={`${testId}-back`}>
            <ChevronLeft size={22} />
          </button>
          <Link to={home} className="flex items-center gap-2 font-bold font-display text-lg min-w-0" data-testid={`${testId}-logo-link`}>
            <YalolaLogo size={28} /> <span className={`truncate ${actions ? "hidden sm:inline" : "hidden min-[380px]:inline"}`}>{appName}</span>
          </Link>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {actions && <div className="hidden sm:flex items-center gap-2" data-testid={`${testId}-actions`}>{actions}</div>}
            <LangFlags testId={testId} />
          </div>
        </div>
        {actions && <div className={`sm:hidden ${maxWidth} mx-auto px-3 pb-2 flex items-center justify-end gap-2`} data-testid={`${testId}-actions-mobile`}>{actions}</div>}
      </header>
      <main className={`${maxWidth} mx-auto w-full px-4 pt-6 pb-10 flex-1 space-y-4`}>
        {title && (
          <div className={icon ? "flex items-start gap-3" : ""}>
            {icon && <span className="h-11 w-11 rounded-2xl grid place-items-center shrink-0 mt-1" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid={`${testId}-icon`}>{icon}</span>}
            <div className="min-w-0">
              <h1 className="font-bold font-display text-3xl sm:text-4xl" style={{ color: "var(--yalola-text)" }} data-testid={`${testId}-title`}>{title}</h1>
              {subtitle && <p className="mt-2 text-base md:text-lg" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-subtitle`}>{subtitle}</p>}
            </div>
          </div>
        )}
        {children}
        {!user && joinLabel && (
          <div className="text-center pt-2">
            <Link to="/?tab=register" className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold" style={{ background: "var(--yalola-primary)", color: "#fff" }} data-testid={`${testId}-join`}>
              {joinLabel} <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </main>
      {footer && <SiteFooter />}
    </div>
  );
};

export default PublicPageShell;
